import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { evaluateIsFoodArticle } from "@coworker/app/src/helpers/utils";
import { useGroupedFilteredLocations } from "@coworker/app/src/components/InputPopup/Location";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { ItemStockAndEds } from "@coworker/apprestructured/src/shared/components/item-cards/ItemStockAndEds/ItemStockAndEds";
import styles from "./AddLocation.module.css";
import ModalColumn from "../ModalColumn/ModalColumn";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";

type AddLocationProps = {
  itemNo: string;
  slid: string; // Code for location
  disabled: boolean;
  isError?: boolean;
  onConfirmClick: (location: string) => void;
};

function AddLocation({
  itemNo,
  slid,
  disabled,
  isError = false,
  onConfirmClick,
}: AddLocationProps) {
  const [showModal, setShowModal] = useState(false);
  const [newSlid, setNewSlid] = useState<string>(slid);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const isFoodArticle = evaluateIsFoodArticle(itemNo);

  const { data: item } = useItemInfo(itemNo);

  const itemFullId = item?.type && item?.no ? `${item.type}${item.no}` : itemNo;

  const { mainLocations, otherLocations, sgfLocations, isLocationsLoading } =
    useGroupedFilteredLocations(itemFullId, searchQuery, isFoodArticle);

  const { mainLocations: allMainLocations, otherLocations: allOtherLocations } =
    useGroupedFilteredLocations(itemFullId, "", isFoodArticle);

  const { t } = useTranslation();

  const handleOpenModal = useCallback(() => {
    setNewSlid(slid);
    setSearchQuery("");
    setShowModal(true);
  }, [slid]);

  const {
    saveGuardedFunction: handleCloseModal,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
    }, [])
  );

  const handleConfirm = useCallback(() => {
    onConfirmClick(newSlid);
    setShowModal(false);
    disableSaveGuard();
  }, [onConfirmClick, newSlid, disableSaveGuard]);

  const handleItemChange = useCallback(
    (nextSlid: string) => {
      setNewSlid(nextSlid);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const totalAssqCapacity = useMemo(() => {
    return [...allMainLocations, ...allOtherLocations].reduce(
      (sum, location) => sum + Number(location.quantity ?? 0),
      0
    );
  }, [allMainLocations, allOtherLocations]);

  const locationSection = useCallback(
    (locations: typeof mainLocations, titleKey: string) => {
      if (isLocationsLoading) return <FixaSkeleton height="100px" />;
      if (locations.length === 0) return null;

      return (
        <div>
          <FixaListViewItem title={t(titleKey)} quantityLabel={<>SSQ</>} />
          {locations.map((location) => (
            <FixaListViewItem
              id={`location-${location.code}`}
              key={location.code}
              title={location.section}
              description={location.code}
              emphasised
              quantityLabel={<>{location.quantity}</>}
              onChange={() => handleItemChange(location.code)}
              control="radiobutton"
              controlProps={{
                checked: location.code === newSlid,
                value: location.code,
              }}
            />
          ))}
        </div>
      );
    },
    [newSlid, isLocationsLoading, t, handleItemChange]
  );

  const mainLocationsContent = useMemo(
    () => locationSection(mainLocations, "homeAndPrimaryString"),
    [mainLocations, locationSection]
  );

  const otherLocationsContent = useMemo(
    () => locationSection(otherLocations, "otherLocationsString"),
    [otherLocations, locationSection]
  );

  const sgfLocationsContent = useMemo(
    () => locationSection(sgfLocations, "sgfLocationsString"),
    [sgfLocations, locationSection]
  );

  const hasAnyLocations =
    mainLocations.length > 0 ||
    otherLocations.length > 0 ||
    sgfLocations.length > 0;

  const totalCapacityContent = useMemo(() => {
    if (isLocationsLoading) return <FixaSkeleton height="50px" />;

    if (!hasAnyLocations) return null;

    return (
      <FixaListViewItem
        className={styles["totalCapacity"]}
        title={`${t("totalCapacityString")} (ASSQ)`}
        quantityLabel={<>{totalAssqCapacity}</>}
        showDivider={false}
        inset
      />
    );
  }, [isLocationsLoading, hasAnyLocations, totalAssqCapacity, t]);

  const hasSearchQuery = searchQuery.trim().length > 0;

  const noResultsContent = useMemo(() => {
    if (isLocationsLoading || hasAnyLocations || !hasSearchQuery) return null;

    return (
      <div className={styles["noResults"]}>
        <FixaText textStyle="Heading.L">{t("noTaskResultsString")}</FixaText>
        <FixaText textStyle="Body.L">
          {t("noResultsForString", { query: searchQuery })}
        </FixaText>
      </div>
    );
  }, [isLocationsLoading, hasAnyLocations, hasSearchQuery, searchQuery, t]);

  return (
    <>
      <FixaListViewItem
        title={t("location3String")}
        inset
        onClick={handleOpenModal}
        emphasised={!!slid}
        description={slid}
        control="navigational"
        controlIcon={null}
        disabled={disabled}
        addOn={
          isError ? <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn> : null
        }
      />
      <FixaModal
        visible={showModal}
        handleCloseBtn={handleCloseModal}
        data-testid="AddLocation-FixaModal"
      >
        <FixaSheets
          header={
            <FixaModalHeader
              title={t("location3String")}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <ModalColumn gap="medium">
              <ItemStockAndEds
                itemNo={item?.no || ""}
                itemType={item?.type || ""}
              />
              <FixaSearch
                id="locations-search"
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery("")}
                placeholder={t("searchLocationString")}
                value={searchQuery}
              />
              {totalCapacityContent}
              <FixaListView id="locations" className={styles["section"]}>
                {mainLocationsContent}
                {otherLocationsContent}
                {sgfLocationsContent}
              </FixaListView>
              {noResultsContent}
            </ModalColumn>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default AddLocation;
